import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Store } from '@ngrx/store';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ViewMode } from '../../../models';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { generateRandomString } from '@utils/string-utils';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { ShopService } from '@services/shops/shop.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
@Component({
  selector: 'gift-note',
  templateUrl: './gift-note.component.html',
  styleUrls: ['./gift-note.component.scss'],
  standalone: false,
})
export class GiftNoteComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  formGroup: UntypedFormGroup;
  readonly formArrayFieldName = 'restrictionsRules';
  restrictionsRules = getExtensionRules();
  entities;
  shopCurrencySymbol: string;
  constructor(
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    private shopService: ShopService,
    private extensionsService: ExtensionsService,
  ) {}
  ngOnDestroy() {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.GIFT_NOTE);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.initForm();
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension.template, extension.usageToken);
      }
    });
    this.entities = this.restrictionsRules.map((r) => r.entity);

    // Report form validity changes
    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  dispatchExampleViewValues(values) {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.GIFT_NOTE]: {
        mode,
        template: { ...values },
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, usageToken?: string) {
    this.formGroup = new UntypedFormGroup({
      usageToken: new UntypedFormControl(usageToken ?? 'gift_note_' + generateRandomString(12)),
      internalName: new UntypedFormControl(loadedValues?.internalName ?? 'Gift note', Validators.required),
      title: new UntypedFormControl(loadedValues?.title ?? 'Gift note'),
      giftMessage: new UntypedFormGroup({
        checkboxText: new UntypedFormControl(loadedValues?.giftMessage?.checkboxText ?? 'This order is a gift'),
        defaultSelected: new UntypedFormControl(loadedValues?.giftMessage?.defaultSelected ?? false),
        inputPlaceholder: new UntypedFormControl(loadedValues?.giftMessage?.inputPlaceholder ?? 'Gift message'),
      }),
      collectEmail: new UntypedFormGroup({
        active: new UntypedFormControl(loadedValues?.collectEmail?.active ?? false),
        inputPlaceholder: new UntypedFormControl(
          loadedValues?.collectEmail?.inputPlaceholder ?? 'Recipient email address',
        ),
      }),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.restrictionsRules ?? [],
        getExtensionRules(),
      ),
    });

    this.dispatchExampleViewValues(this.formGroup.value);
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  handleSave() {
    const formValue = this.formGroup.value;
    const { usageToken, ...template } = formValue;
    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.GIFT_NOTE,
    };
    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }
}
